import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Modal, Textarea } from '@noor/ui';
import type { ModalRenderProps } from '@noor/lib';
import { Can } from '@/entities/access';
import { chatMacroFormSchema, MACRO_MESSAGE_MAX, MACRO_TITLE_MAX } from '../model/schema';
import { useCreateChatMacro, useUpdateChatMacro } from '../model/use-chat-macros';
import { MacroLangToggle } from './macro-lang-toggle';

/**
 * Create / edit a macro (cpanel's two macro dialogs, merged): the payload's
 * `macro` decides the mode. Title and message are required and trimmed.
 */
export function ChatMacroModal({ payload, close }: ModalRenderProps<'chat-macro'>) {
  const { t } = useTranslation('inbox');
  const editing = payload.macro;
  const [lang, setLang] = useState(payload.lang);
  const [title, setTitle] = useState(editing?.title ?? '');
  const [message, setMessage] = useState(editing?.message ?? '');
  const create = useCreateChatMacro();
  const update = useUpdateChatMacro();
  const pending = create.isPending || update.isPending;

  const parsed = chatMacroFormSchema.safeParse({ lang, title, message });

  const submit = () => {
    if (!parsed.success) return;
    if (editing) {
      update.mutate({ id: editing.id, body: parsed.data }, { onSuccess: close });
    } else {
      create.mutate(parsed.data, { onSuccess: close });
    }
  };

  return (
    <Can permission="inbox.macros.manage">
      <Modal
        title={editing ? t('macros.editTitle') : t('macros.createTitle')}
        description={editing ? t('macros.editHint') : t('macros.createHint')}
        dismissible={!pending}
        footer={
          <>
            <Button
              variant="soft"
              size="modal"
              shape="rounded"
              onClick={close}
              disabled={pending}
            >
              {t('macros.cancel')}
            </Button>
            <Button
              variant="dark"
              size="modal"
              shape="rounded"
              onClick={submit}
              disabled={pending || !parsed.success}
            >
              {editing ? t('macros.save') : t('macros.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <MacroLangToggle value={lang} onChange={setLang} />
          <div className="space-y-1.5">
            <Label htmlFor="macro-title" className="px-[var(--form-label-padding-x)]">
              {t('macros.fieldTitle')}
            </Label>
            <Input
              id="macro-title"
              value={title}
              maxLength={MACRO_TITLE_MAX}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="macro-message" className="px-[var(--form-label-padding-x)]">
              {t('macros.fieldMessage')}
            </Label>
            <Textarea
              id="macro-message"
              rows={5}
              value={message}
              maxLength={MACRO_MESSAGE_MAX}
              onChange={(event) => setMessage(event.target.value)}
            />
          </div>
        </div>
      </Modal>
    </Can>
  );
}
